import { FC, useCallback } from 'react';
import { IntlConfig } from 'react-intl';
import { defaultLocale, LanguageProvider, LocaleContext, LoadMessages, LanguageProviderProps as IntlLanguageProviderProps } from '@cld/intl';
import { loadMessages } from '../';
import { useUserData } from '../../context';

export type LanguageProviderProps = Partial<Omit<IntlLanguageProviderProps, 'loadMessages'>> & {
  locale?: string;
  loadAppMessages?: LoadMessages;
};

/**
 * `translationOverrides` expects on object with the following structure:
 * {
 *    [localeCode]: {
 *        // messages
 *    }
 * }
 */

export const ApplicationWrapperLanguageProvider: FC<LanguageProviderProps> = ({ locale, ...rest }) => {
  const { userData } = useUserData();
  return (
    <LocaleContext.Provider value={locale || userData?.userEffectiveLanguage || defaultLocale}>
      <ExtendedLanguageProvider {...rest} />
    </LocaleContext.Provider>
  );
};

const ExtendedLanguageProvider: FC<LanguageProviderProps> = ({ loadAppMessages, children, ...props }) => {
  const loadAllMessages = useCallback(
    (locale: string) => Promise.all([loadMessages(locale), loadAppMessages?.(locale)]).then(reduceMessages),
    [loadAppMessages]
  );

  return (
    <LanguageProvider {...props} loadMessages={loadAllMessages}>
      {children}
    </LanguageProvider>
  );
};

const notEmpty = <TValue,>(value: TValue | null | undefined): value is TValue => value !== null && value !== undefined;

const reduceMessages = (messagesArray: Array<IntlConfig['messages']>) =>
  messagesArray.filter(notEmpty).reduce((acc, loadedMessages) => Object.assign(acc, loadedMessages), {});
